/**
 * Browsers that will not open an App Store URL.
 *
 * Meta's iOS webviews show a blank page; TikTok shows an error toast. Every
 * other browser, and all of Android, follows store links normally and must
 * keep the plain redirect — on Android the Play Store URL also carries the
 * install referrer, which an escape page would throw away.
 */
export type BrokenInAppBrowser =
  "instagram" | "threads" | "facebook" | "tiktok";

const IOS_UA_PATTERN = /iphone|ipad|ipod/;

// No TikTok build says "tiktok": the app still identifies as musical_ly, the
// international builds as Trill, and all of them carry ByteDance's token.
const TIKTOK_UA_PATTERN = /musical_ly|bytedancewebview|trill_|aweme/;

/**
 * Threads user agents also contain "instagram", so "barcelona" (its codename)
 * has to be tested first or every Threads visitor is read as Instagram.
 */
export function detectBrokenInAppBrowser(
  userAgent: string
): BrokenInAppBrowser | null {
  const ua = userAgent.toLowerCase();

  if (ua.includes("barcelona")) return "threads";
  if (ua.includes("instagram")) return "instagram";
  // Messenger sends the same user agent (FBOP/80 where Facebook has FBOP/5)
  // and its webview behaves the same, so it counts as Facebook.
  if (ua.includes("fban") || ua.includes("fbav") || ua.includes("fb_iab")) {
    return "facebook";
  }
  if (TIKTOK_UA_PATTERN.test(ua)) return "tiktok";

  return null;
}

/**
 * True where no escape can work, so the screen tells the visitor to leave the
 * webview themselves.
 *
 * Measured on TikTok on 2026-09-10: a bio link refused a plain navigation to
 * the store, a redirect through our own host, `itms-apps`, `x-safari` and
 * `googlechromes` alike. TikTok checks where a navigation ends, so nothing
 * pointing at the App Store gets through. Trying anyway costs the visitor five
 * seconds of error toasts.
 */
export function cannotEscape(browser: BrokenInAppBrowser): boolean {
  return browser === "tiktok";
}

/**
 * True where a store link opens fine from a real tap, so a tap needs no
 * escape. Only the automatic redirect, which has no tap, is blocked there.
 *
 * Measured on 2026-10-13 (iPhone 11, iOS 26.5.2, Facebook and Messenger
 * 582.0.0): a tapped App Store link opened the App Store in both apps. In
 * Messenger so did location.replace run from a tap, also after awaiting a
 * request, while the same redirect with no tap did nothing. window.open of an
 * x-safari URL, which the Facebook chain used to fire on a tap, left the
 * visitor on a blank window inside the webview in both apps.
 */
export function opensStoreLinksOnTap(browser: BrokenInAppBrowser): boolean {
  return browser === "facebook";
}

/** Null means "keep the normal redirect". Only iOS is affected. */
export function detectBrokenIosInAppBrowser(
  userAgent: string
): BrokenInAppBrowser | null {
  if (!IOS_UA_PATTERN.test(userAgent.toLowerCase())) return null;
  return detectBrokenInAppBrowser(userAgent);
}
